import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import fetchData from '@/utils/fetchData';
import useError from '@/hooks/useError';

type Props = {
  children: ReactNode;
};

type TermLimit = {
  amountFrom: number;
  amountTo: number;
  termFrom: number;
  termTo: number;
  limitType: 'SPL' | 'IL';
};
export type ClientConstraints = {
  amountInterval: {
    min: number;
    max: number;
    step: number;
    defaultValue: number;
  };
  termInterval: {
    min: number;
    max: number;
    step: number;
    defaultValue: number;
  };
  termLimits: Array<TermLimit>;
};

export type ClientConstraintsContextType = {
  clientConstraints: ClientConstraints | undefined;
  fetchClientConstraints: () => Promise<ClientConstraints | void>;
  setClientConstraints: React.Dispatch<
    React.SetStateAction<ClientConstraints | undefined>
  >;
};

export const ClientConstraintsContext =
  createContext<ClientConstraintsContextType>(
    {} as ClientConstraintsContextType,
  );

export function ClientConstraintsProvider({
  children,
}: Props): React.JSX.Element {
  const [clientConstraints, setClientConstraints] = useState<
    ClientConstraints | undefined
  >();
  const { showError } = useError();

  /** Fetches client calculator constraints based on product number. */
  const fetchClientConstraints =
    useCallback(async (): Promise<ClientConstraints | void> => {
      try {
        const fetchedClientConstraints: ClientConstraints = await fetchData(
          '/client/application/constraints',
        );

        setClientConstraints(fetchedClientConstraints);

        return fetchedClientConstraints;
      } catch (e) {
        showError();
        throw e;
      }
    }, [showError]);

  const clientContextValue = useMemo(
    () => ({
      clientConstraints,
      fetchClientConstraints,
      setClientConstraints,
    }),
    [clientConstraints, fetchClientConstraints],
  );

  return (
    <ClientConstraintsContext.Provider value={clientContextValue}>
      {children}
    </ClientConstraintsContext.Provider>
  );
}
